import React, { useState, useEffect } from "react";
import { useToast } from "@chakra-ui/react";
import { updatePackage } from "action/packageAction";

const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/>
  </svg>
);
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="11" height="11">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const DatabaseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
    <ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
  </svg>
);

function EditPackageModal({ isOpen, onClose, package: pkg, onSuccess }) {
  const [formData, setFormData] = useState({ name: "", price: 0, storage_gb: 1, benefits: [""] });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (pkg) {
      setFormData({ name: pkg.name || "", price: pkg.price || 0, storage_gb: pkg.storage_gb || 1, benefits: pkg.benefits?.length > 0 ? pkg.benefits : [""] });
    } else {
      setFormData({ name: "", price: 0, storage_gb: 1, benefits: [""] });
    }
    setErrors({});
  }, [pkg]);

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const updateBenefit = (i, v) => {
    const next = [...formData.benefits];
    next[i] = v;
    setFormData((prev) => ({ ...prev, benefits: next }));
  };

  const addBenefit = () => setFormData((prev) => ({ ...prev, benefits: [...prev.benefits, ""] }));

  const removeBenefit = (i) => {
    if (formData.benefits.length > 1)
      setFormData((prev) => ({ ...prev, benefits: prev.benefits.filter((_, idx) => idx !== i) }));
  };

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = "Nama package wajib diisi";
    if (formData.price <= 0) errs.price = "Harga harus lebih dari 0";
    if (!formData.storage_gb || formData.storage_gb < 1) errs.storage_gb = "Storage minimal 1 GB";
    if (!formData.benefits.some((b) => b.trim())) errs.benefits = "Minimal satu benefit diperlukan";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const result = await updatePackage(pkg.id, { ...formData, benefits: formData.benefits.filter((b) => b.trim()) });
      if (result.success) {
        toast({ title: "Package Diperbarui", description: result.message || "Package berhasil diperbarui.", status: "success", duration: 3000, isClosable: true });
        if (onSuccess) onSuccess(result.data);
        onClose();
      } else {
        toast({ title: "Error", description: result.message || "Failed to update package", status: "error", duration: 3000, isClosable: true });
      }
    } catch {
      toast({ title: "Error", description: "An error occurred while updating package", status: "error", duration: 3000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  const validBenefits = formData.benefits.filter((b) => b.trim());

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)", zIndex: 90, overflow: "auto" }}
      onClick={onClose}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100%", padding: "40px 20px" }}>
      <div
        style={{ background: "#fff", borderRadius: 20, boxShadow: "0 24px 48px -12px rgba(15,23,42,0.18)", width: "100%", maxWidth: 780, display: "flex", flexDirection: "column" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--border-subtle)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: "var(--warning-50, #fffbeb)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--warning-600, #d97706)", flexShrink: 0 }}>
              <EditIcon />
            </div>
            <div>
              <div className="t-h2">Edit Package</div>
              <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 1 }}>{pkg?.name || "—"}</div>
            </div>
          </div>
          <button style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", display: "flex", alignItems: "center", padding: 6, borderRadius: 8 }} onClick={onClose}>
            <XIcon />
          </button>
        </div>

        {/* Two-column body */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
          {/* Left: form */}
          <div style={{ padding: "20px 24px", borderRight: "1px solid var(--border-subtle)", display: "flex", flexDirection: "column", gap: 16 }}>
            <div className={`lms-form-group ${errors.name ? "has-error" : ""}`} style={{ marginBottom: 0 }}>
              <label className="lms-label">Nama Package <span style={{ color: "var(--danger-500)" }}>*</span></label>
              <input className="lms-input" value={formData.name} onChange={(e) => handleChange("name", e.target.value)} placeholder="Nama package" />
              {errors.name && <div className="lms-error-msg">{errors.name}</div>}
            </div>

            <div className={`lms-form-group ${errors.price ? "has-error" : ""}`} style={{ marginBottom: 0 }}>
              <label className="lms-label">Harga / Bulan (IDR) <span style={{ color: "var(--danger-500)" }}>*</span></label>
              <input
                className="lms-input"
                type="number"
                min={0}
                value={formData.price}
                onChange={(e) => handleChange("price", parseInt(e.target.value) || 0)}
                placeholder="0"
              />
              {errors.price && <div className="lms-error-msg">{errors.price}</div>}
            </div>

            <div className={`lms-form-group ${errors.storage_gb ? "has-error" : ""}`} style={{ marginBottom: 0 }}>
              <label className="lms-label">Storage (GB) <span style={{ color: "var(--danger-500)" }}>*</span></label>
              <input
                className="lms-input"
                type="number"
                min={1}
                value={formData.storage_gb}
                onChange={(e) => handleChange("storage_gb", parseInt(e.target.value) || 1)}
                placeholder="Contoh: 10"
              />
              {errors.storage_gb && <div className="lms-error-msg">{errors.storage_gb}</div>}
            </div>

            <div className={`lms-form-group ${errors.benefits ? "has-error" : ""}`} style={{ marginBottom: 0 }}>
              <label className="lms-label">Daftar Benefit</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 260, overflowY: "auto", paddingRight: 2 }}>
                  {formData.benefits.map((b, i) => (
                    <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <span style={{ width: 20, height: 20, borderRadius: "50%", background: "var(--slate-100)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "var(--text-muted)", flexShrink: 0 }}>
                        {i + 1}
                      </span>
                      <input className="lms-input" value={b} onChange={(e) => updateBenefit(i, e.target.value)} placeholder={`Benefit ${i + 1}`} style={{ flex: 1 }} />
                      {formData.benefits.length > 1 && (
                        <button className="lms-btn lms-btn-ghost lms-btn-sm lms-btn-icon" style={{ color: "var(--danger-500)", flexShrink: 0 }} onClick={() => removeBenefit(i)}>
                          <TrashIcon />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button className="lms-btn lms-btn-secondary lms-btn-sm" style={{ alignSelf: "flex-start" }} onClick={addBenefit}>
                  <PlusIcon /> Tambah Benefit
                </button>
              </div>
              {errors.benefits && <div className="lms-error-msg">{errors.benefits}</div>}
            </div>
          </div>

          {/* Right: live preview */}
          <div style={{ padding: "20px 24px", background: "var(--slate-50)", display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-muted)" }}>Preview</div>
            <div style={{ background: "#fff", border: "1px solid var(--border-subtle)", borderRadius: 16, padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ height: 4, background: "linear-gradient(90deg,var(--brand-500),var(--brand-400))", margin: "-20px -20px 0", borderRadius: "16px 16px 0 0" }} />
              <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text-strong)" }}>{formData.name || <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>Nama Package</span>}</div>
              <div>
                <div style={{ fontSize: 24, fontWeight: 800, color: "var(--brand-600)", letterSpacing: "-0.02em" }}>
                  Rp {(formData.price || 0).toLocaleString("id-ID")}
                </div>
                <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>per bulan</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--text-muted)", background: "var(--slate-100)", borderRadius: 8, padding: "5px 10px", alignSelf: "flex-start" }}>
                <DatabaseIcon />
                <span style={{ fontWeight: 600, color: "var(--text-strong)" }}>{formData.storage_gb || 0} GB</span>
                <span>Storage</span>
              </div>
              {validBenefits.length > 0 ? (
                <div style={{ borderTop: "1px solid var(--border-subtle)", paddingTop: 12, display: "flex", flexDirection: "column", gap: 7 }}>
                  {validBenefits.map((b, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13 }}>
                      <span style={{ width: 17, height: 17, borderRadius: "50%", background: "var(--success-100)", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--success-600)", flexShrink: 0, marginTop: 1 }}>
                        <CheckIcon />
                      </span>
                      {b}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: "var(--text-muted)", fontSize: 13, fontStyle: "italic" }}>Belum ada benefit</div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 24px", borderTop: "1px solid var(--border-subtle)", display: "flex", justifyContent: "flex-end", gap: 8, flexShrink: 0 }}>
          <button className="lms-btn lms-btn-secondary" onClick={onClose} disabled={loading}>Batal</button>
          <button className="lms-btn lms-btn-primary" onClick={handleSave} disabled={loading}>
            {loading ? <span className="lms-spinner" style={{ width: 14, height: 14 }} /> : <EditIcon />}
            {loading ? "Menyimpan..." : "Simpan Perubahan"}
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}

export default EditPackageModal;
